/**
 * O detalhe de uma organização ligado ao servidor (#767): lê a organização e o
 * histórico, salva o contrato e o cadastro (#769), e encerra, reabre ou exclui.
 *
 * A tela (`TelaDaOrganizacao`) nasceu antes do back e recebe tudo pelas props;
 * esta é quem a monta. Salvou, o contrato que o servidor devolveu entra na
 * tela na hora, o detalhe, o histórico e a lista são lidos de novo, a entrada
 * nova do histórico destaca, e o aviso confirma. O cadastro, do mesmo jeito.
 *
 * Encerrar, reabrir e excluir abrem o diálogo que diz o que a ação faz e pede
 * o motivo (`DialogoDeSituacao`). Excluir passa ainda pelo segundo fator: o
 * servidor responde pedindo a confirmação, o diálogo dela abre por cima, com o
 * motivo já escrito, e a exclusão é refeita depois. O "Retomar" da organização
 * em criação continua a criação com o pedido que ela guardou (#769), e entrega o
 * primeiro acesso do administrador, quando há.
 *
 * "Sem permissão" e "não encontrado" encerram a tela, mesmo com o que já estava
 * na memória: a organização que a concessão de Super Admin, retirada com a ficha
 * aberta, deixou de alcançar não continua à vista depois da recusa.
 */

import { useState, type ReactNode } from 'react';
import type {
  OrganizacaoDetalhada as OrganizacaoDaApi,
  PrimeiroAcesso,
  RevendaDesprovisionada,
} from '@cliqcrm/contracts';
import { DialogoDeStepUp, useStepUp } from '@/features/acesso/confirmar-identidade';
import { PrimeiroAcessoDaPessoa } from '@/entities/acesso/pessoa';
import { chaves } from '@/shared/api/chaves';
import { useApi, useHistoricoDaOrganizacao, useOrganizacaoNaGestao } from '@/shared/api/consultas';
import { useInvalidar, useQueryClient } from '@/shared/api/provedor';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { avisar } from '@/shared/ui';
import { ErroDaApi } from '@cliqcrm/contracts';
import { corpoDoCadastro } from '../lib/cadastro';
import { corpoDoContrato } from '../lib/contrato';
import { encerraATela } from '../lib/recusa';
import { depoisDaResposta, estadoDaFalha, type EstadoDaRetomada } from '../lib/retomada';
import {
  AVISO_DA_ACAO,
  resultadoDaAcao,
  terminouLimpo,
  type AcaoDeSituacao,
  type ResultadoDaAcao,
} from '../lib/situacao';
import {
  organizacaoDetalhada,
  type MudancaDoCadastro,
  type MudancaDoContrato,
} from '../model/organizacao-detalhada';
import { DialogoDeSituacao } from './dialogo-de-situacao';
import { TelaDaOrganizacao } from './tela-da-organizacao';

export type PaginaDaOrganizacaoProps = {
  id: string;
  aoVoltar: () => void;
  /** Leva ao formulário de "Nova organização", quando o pedido da criação não está mais guardado. */
  aoAbrirNovaOrganizacao?: () => void;
};

type DialogoAberto = {
  readonly acao: AcaoDeSituacao;
  readonly enviando: boolean;
  readonly falha: string | null;
  readonly resultado: ResultadoDaAcao | null;
};

const FALHA_GENERICA: Readonly<Record<AcaoDeSituacao, string>> = {
  encerrar: 'Não consegui encerrar a organização.',
  reabrir: 'Não consegui reabrir a organização.',
  excluir: 'Não consegui excluir a organização.',
};

export function PaginaDaOrganizacao({
  id,
  aoVoltar,
  aoAbrirNovaOrganizacao,
}: PaginaDaOrganizacaoProps): ReactNode {
  const consulta = useOrganizacaoNaGestao(id);
  const historico = useHistoricoDaOrganizacao(id);
  const api = useApi();
  const invalidar = useInvalidar();
  const cache = useQueryClient();
  const stepUp = useStepUp();
  const [novaNoHistorico, definirNovaNoHistorico] = useState<string | null>(null);
  const [dialogo, definirDialogo] = useState<DialogoAberto | null>(null);
  const [retomada, definirRetomada] = useState<EstadoDaRetomada | null>(null);
  // O que sobrou para fazer depois de uma retomada que terminou (#769).
  const [pendenciasDaCriacao, definirPendencias] = useState<readonly string[]>([]);
  // O primeiro acesso do administrador que a retomada criou: aparece uma vez.
  const [acesso, definirAcesso] = useState<{ nome: string; acesso: PrimeiroAcesso } | null>(null);
  /** A recusa de uma ação ou de um salvamento que fecha a tela (#1160). */
  const [recusaDaAcao, definirRecusaDaAcao] = useState<unknown>(null);
  /** A justificativa que o segundo fator concedeu à exclusão aberta (#1160). */
  const [justificativaConcedida, definirConcedida] = useState<string | null>(null);
  const recusa = [consulta.error, historico.error, recusaDaAcao].find(encerraATela);
  /**
   * Fecha a tela pela recusa de uma ação ou de um salvamento. O detalhe e o
   * histórico saem também do cache: aberta de novo, a ficha não reaparece com o
   * que havia antes da recusa enquanto a leitura nova não volta (#1160). Com a
   * página ainda montada, as duas consultas leem de novo na hora e guardam a
   * mesma recusa: é um pedido a mais, para o cache não ficar com a ficha.
   */
  const encerrarPelaRecusa = (erro: unknown): void => {
    definirRecusaDaAcao(erro);
    cache.removeQueries({ queryKey: chaves.organizacaoNaGestao(id), exact: true });
    cache.removeQueries({ queryKey: chaves.historicoDaOrganizacao(id), exact: true });
  };
  /** A recusa que fecha a tela também quando vem de quem salva: o painel a mostra, e a tela sai. */
  const fecharSeRecusou = (erro: unknown): never => {
    if (encerraATela(erro)) encerrarPelaRecusa(erro);
    throw erro;
  };
  const organizacao = !recusa && consulta.data ? organizacaoDetalhada(consulta.data) : undefined;

  async function salvarContrato(mudanca: MudancaDoContrato): Promise<void> {
    if (!api) throw new Error('Sem sessão.');
    const resposta = await api
      .alterarContratoDaOrganizacao({
        caminho: { id },
        corpo: corpoDoContrato(mudanca),
      })
      .catch(fecharSeRecusou);
    // O contrato salvo aparece já, sem esperar a releitura: a tela não mostra o antigo.
    cache.setQueryData<OrganizacaoDaApi>(chaves.organizacaoNaGestao(id), (anterior) =>
      anterior ? { ...anterior, contrato: resposta.contrato } : anterior,
    );
    definirNovaNoHistorico(resposta.entrada_no_historico);
    invalidar([
      chaves.organizacaoNaGestao(id),
      chaves.historicoDaOrganizacao(id),
      chaves.organizacoesNaGestao(),
    ]);
    avisar(
      resposta.entrada_no_historico
        ? 'Contrato salvo. A mudança foi para o histórico.'
        : 'Nada mudou no contrato.',
    );
  }

  async function salvarCadastro(mudanca: MudancaDoCadastro): Promise<void> {
    if (!api) throw new Error('Sem sessão.');
    const resposta = await api
      .alterarCadastroDaOrganizacao({
        caminho: { id },
        corpo: corpoDoCadastro(mudanca),
      })
      .catch(fecharSeRecusou);
    // O cadastro salvo aparece já, sem esperar a releitura: a tela não mostra o antigo.
    cache.setQueryData<OrganizacaoDaApi>(chaves.organizacaoNaGestao(id), (anterior) =>
      anterior
        ? {
            ...anterior,
            cadastro: resposta.cadastro,
            // A resposta só traz o contato quando o pedido o trouxe. Apagado, não
            // sobra nem o guardado até a purga da excluída.
            ...(resposta.contato_financeiro === undefined
              ? {}
              : {
                  contato_financeiro: resposta.contato_financeiro,
                  ...(resposta.contato_financeiro === null
                    ? { contato_financeiro_ate_a_purga: false }
                    : {}),
                }),
          }
        : anterior,
    );
    definirNovaNoHistorico(resposta.entrada_no_historico);
    invalidar([
      chaves.organizacaoNaGestao(id),
      chaves.historicoDaOrganizacao(id),
      chaves.organizacoesNaGestao(),
    ]);
    avisar(
      resposta.entrada_no_historico === null
        ? 'Nada mudou no cadastro.'
        : mudanca.contatoFinanceiro === null
          ? 'Contato financeiro removido. A mudança foi para o histórico.'
          : 'Cadastro salvo. A mudança foi para o histórico.',
    );
  }

  /**
   * Retoma a criação com o pedido guardado (#769). Terminou: a faixa sai já, a
   * tela relê e avisa, e o primeiro acesso do administrador aparece uma vez. Não
   * terminou, o caminho do formulário e a falha ficam na faixa da criação.
   */
  async function retomar(): Promise<void> {
    if (!api) return;
    definirRetomada({ fase: 'retomando' });
    // As pendências de uma retomada anterior saem: as desta tomam o lugar.
    definirPendencias([]);
    try {
      const saida = await api.retomarCriacaoDaOrganizacao({ caminho: { id }, corpo: {} });
      if (saida.status === 'ativo') {
        // Ativa já, sem esperar a releitura: a tela não oferece retomar o que terminou.
        cache.setQueryData<OrganizacaoDaApi>(chaves.organizacaoNaGestao(id), (anterior) =>
          anterior
            ? { ...anterior, situacao: 'ativa', parou_em: null, pedido_guardado_ate: null }
            : anterior,
        );
      }
      invalidar([
        chaves.organizacaoNaGestao(id),
        chaves.historicoDaOrganizacao(id),
        chaves.organizacoesNaGestao(),
      ]);
      const depois = depoisDaResposta(saida);
      definirRetomada(depois.retomada);
      definirPendencias(depois.pendencias);
      if (saida.status === 'ativo') {
        avisar('A criação terminou: a organização está ativa.');
        // O nome vem com o primeiro acesso; sem ele, a senha ainda aparece.
        if (saida.primeiroAcesso) {
          definirAcesso({
            nome: saida.nome_do_administrador ?? 'O administrador',
            acesso: saida.primeiroAcesso,
          });
        }
      }
    } catch (falha) {
      // "Sem permissão" ou "não encontrada" fecha a tela, como nas outras ações (#1160).
      if (encerraATela(falha)) {
        definirRetomada(null);
        encerrarPelaRecusa(falha);
        return;
      }
      /*
       * As outras falhas releem o detalhe e o histórico, antes de oferecer o
       * botão de novo: a criação pode ter terminado em outra aba, ou a resposta
       * pode ter caído no caminho com a criação ainda rodando (a trava da
       * criação é a #1135). O
       * pedido que não está mais guardado volta da releitura como o caminho do
       * formulário.
       */
      invalidar([chaves.organizacaoNaGestao(id), chaves.historicoDaOrganizacao(id)]);
      const estado = estadoDaFalha(falha);
      definirRetomada(estado === 'reler' ? null : estado);
    }
  }

  const abrir = (acao: AcaoDeSituacao) => {
    definirConcedida(null);
    definirDialogo({ acao, enviando: false, falha: null, resultado: null });
  };

  /** O que a ação devolveu: a tela relê o que mudou, e o diálogo fecha ou mostra o que sobrou. */
  function concluir(acao: AcaoDeSituacao, resposta: RevendaDesprovisionada): void {
    invalidar([
      chaves.organizacaoNaGestao(id),
      chaves.historicoDaOrganizacao(id),
      chaves.organizacoesNaGestao(),
    ]);
    // O que faltava da criação deixa de ser pendência da tela: a situação mudou.
    definirPendencias([]);
    const resultado = resultadoDaAcao(acao, resposta);
    if (terminouLimpo(resultado)) {
      definirDialogo(null);
      avisar(AVISO_DA_ACAO[acao]);
      return;
    }
    definirDialogo({ acao, enviando: false, falha: null, resultado });
  }

  function falhou(acao: AcaoDeSituacao, erro: unknown): void {
    // "Sem permissão" ou "não encontrada" numa ação fecha a tela, como na
    // leitura (#1160): a ficha que a recusa barrou não fica à vista.
    if (encerraATela(erro)) {
      definirDialogo(null);
      encerrarPelaRecusa(erro);
      return;
    }
    // A recusa por estado diz que a ficha na tela está velha (excluída em outra
    // aba, por exemplo): ela é relida, para não continuar oferecendo o botão.
    if (erro instanceof ErroDaApi && erro.codigo === 'CONFLITO_DE_ESTADO') {
      invalidar([
        chaves.organizacaoNaGestao(id),
        chaves.historicoDaOrganizacao(id),
        chaves.organizacoesNaGestao(),
      ]);
    }
    definirDialogo({
      acao,
      enviando: false,
      falha: recadoDeErro(erro, { generica: FALHA_GENERICA[acao] }),
      resultado: null,
    });
  }

  async function executar(acao: AcaoDeSituacao, justificativa: string | null): Promise<void> {
    if (!api) return;
    definirDialogo({ acao, enviando: true, falha: null, resultado: null });
    const pedido = async (concedida?: string): Promise<void> => {
      // Refeita depois do segundo fator, a exclusão leva a justificativa que ele
      // concedeu: se a pessoa a corrigiu ali, é a corrigida (#1160).
      const corpo = { justificativa: concedida ?? justificativa ?? '' };
      // E ela toma o campo do diálogo: repetida depois de uma falha, enquanto a
      // concessão vale, a exclusão leva a mesma, e não a escrita antes.
      if (concedida !== undefined) definirConcedida(concedida);
      // Também quando o segundo fator refaz a exclusão: enquanto ela roda, o
      // diálogo diz "Excluindo…" e não deixa clicar de novo.
      definirDialogo((atual) =>
        atual && atual.acao === acao ? { ...atual, enviando: true, falha: null } : atual,
      );
      try {
        const resposta =
          acao === 'encerrar'
            ? await api.encerrarRevenda({ caminho: { id }, corpo })
            : acao === 'reabrir'
              ? await api.reabrirRevenda({ caminho: { id } })
              : await api.excluirRevenda({ caminho: { id }, corpo });
        concluir(acao, resposta);
      } catch (erro) {
        // Toda volta sem resposta tira o "Excluindo…", também o segundo fator
        // pedido de novo na exclusão refeita (#1160): sem isto, cancelá-lo
        // deixava o diálogo preso. Quem trata o erro decide o resto.
        definirDialogo((atual) =>
          atual && atual.acao === acao && atual.enviando ? { ...atual, enviando: false } : atual,
        );
        throw erro;
      }
    };
    try {
      if (acao === 'excluir') {
        // O segundo fator: sem a concessão, o servidor pede a confirmação, o
        // diálogo dela abre com o motivo já escrito, e a exclusão é refeita.
        await stepUp.comStepUp(
          pedido,
          id,
          (erro) => falhou(acao, erro),
          justificativa ?? undefined,
        );
      } else {
        await pedido();
      }
    } catch (erro) {
      falhou(acao, erro);
    }
  }

  return (
    <>
      <TelaDaOrganizacao
        organizacao={organizacao}
        historico={recusa ? undefined : historico.data?.itens}
        erro={recusa ?? consulta.error}
        erroDoHistorico={historico.error}
        aoRepetir={() => {
          void consulta.refetch();
          void historico.refetch();
        }}
        aoVoltar={aoVoltar}
        aoSalvarContrato={salvarContrato}
        aoSalvarCadastro={salvarCadastro}
        aoEncerrar={() => abrir('encerrar')}
        aoReabrir={() => abrir('reabrir')}
        aoExcluir={() => abrir('excluir')}
        aoRetomar={() => void retomar()}
        retomada={retomada}
        pendenciasDaCriacao={pendenciasDaCriacao}
        {...(aoAbrirNovaOrganizacao ? { aoAbrirNovaOrganizacao } : {})}
        novaNoHistorico={novaNoHistorico}
      />
      {acesso ? (
        <PrimeiroAcessoDaPessoa
          nome={acesso.nome}
          acesso={acesso.acesso}
          // Fechado o diálogo, a senha sai também da memória da tela.
          aoFechar={() => definirAcesso(null)}
        />
      ) : null}
      {dialogo && organizacao ? (
        <DialogoDeSituacao
          key={dialogo.acao}
          acao={dialogo.acao}
          organizacao={organizacao}
          enviando={dialogo.enviando}
          falha={dialogo.falha}
          resultado={dialogo.resultado}
          justificativaConcedida={justificativaConcedida}
          aoConfirmar={(justificativa) => void executar(dialogo.acao, justificativa)}
          aoFechar={() => definirDialogo(null)}
        />
      ) : null}
      <DialogoDeStepUp uso={stepUp} />
    </>
  );
}
